import { Button } from "@/atoms-react/button/Button.react";
import InputLabel from "@/atoms-react/input/Input.react";

import { RadioCard } from "@/atoms-react/radio-card/index.react";
import { Label, SectionTitle } from "@/atoms-react/text/Text.react";
import { FindOlkypassRelatedPersonCompanyType } from "@/features/company/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { Themes } from "@/types/theme";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

type AddCompanyFormPrps = {
  onValidate: (data: FindOlkypassRelatedPersonCompanyType) => Promise<void>;
  isRepresentative: boolean;
  isUpdatePending: boolean;
};
export const FindAndAddCompanyRelatedLinkForm = ({
  onValidate,
  isRepresentative = false,
  isUpdatePending = false
}: AddCompanyFormPrps) => {
  const { t } = useTranslation(["pass"]);
  const labelInputDivClassName = "md:flex items-center md:space-x-6";
  const labelClassName = "flex w-40 min-w-40 mb-2 md:mb-0";
  const inputClassName = "flex grow";
  const { getLanguage } = usei18n();
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch
  } = useForm<FindOlkypassRelatedPersonCompanyType>({
    defaultValues: {
      hasOlkypass: undefined,
      idOlkypass: null
    }
  });
  const onSubmit: SubmitHandler<FindOlkypassRelatedPersonCompanyType> = (data) => {
    onValidate(data);
  };
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mt-5 flex flex-col justify-between border-2 border-slate-50 px-6 py-5 dark:border-slate-700">
        {isRepresentative ? (
          <div className="mb-10 flex w-full flex-row justify-center p-4">
            <SectionTitle>{t("business_links.add_link.title.agent")}</SectionTitle>
          </div>
        ) : (
          <div className="mb-10 flex w-full flex-row justify-center px-6">
            <SectionTitle>{t("business_links.add_link.title.representative")}</SectionTitle>
          </div>
        )}
        <div className={twMerge(labelInputDivClassName, "mt-4")}>
          <Label className={labelClassName}>
            {isRepresentative
              ? t("business_links.add_link.input.hasOlkypass.agent")
              : t("business_links.add_link.input.hasOlkypass.beneficialOwner")}
          </Label>
          <Controller
            control={control}
            rules={{
              validate: (x) => {
                return x !== undefined;
              }
            }}
            name="hasOlkypass"
            render={({ field: { onChange, value } }) => (
              <div className="flex w-full flex-col">
                <div className={inputClassName + "grid grid-cols-1 gap-2 md:grid-cols-2"}>
                  <RadioCard
                    className={twMerge(
                      "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                      errors.hasOlkypass && "border-red-500"
                    )}
                    active={value == true}
                    onClick={() => onChange(true)}
                  >
                    {getLanguage() === "fr" ? "Oui" : "Yes"}
                  </RadioCard>
                  <RadioCard
                    className={twMerge(
                      "border-2 border-slate-50 dark:border-slate-700 dark:text-white",
                      errors.hasOlkypass && "border-red-500"
                    )}
                    active={value == false}
                    onClick={() => onChange(false)}
                  >
                    {getLanguage() === "fr" ? "Non" : "No"}
                  </RadioCard>
                </div>
              </div>
            )}
          />
        </div>
        <div className={twMerge(labelInputDivClassName, "mt-2")}>
          <Label className={labelClassName}>{t("business_links.add_link.input.idPass")} </Label>
          <div className={inputClassName}>
            <Controller
              control={control}
              rules={{
                required: watch("hasOlkypass") === true
              }}
              name="idOlkypass"
              render={({ field: { onChange, value } }) => (
                <InputLabel
                  data-tooltip={t("business_links.add_link.input.idPass")}
                  placeholder="01234-01234-01234-01234-01234-01234"
                  value={value ?? undefined}
                  error={errors.idOlkypass ? t("atoms:general.required") : ""}
                  required={false}
                  onChange={onChange}
                />
              )}
            />
          </div>
        </div>
        <div className="mb-4 flex w-full flex-row justify-center">
          <Button
            className="mt-5 w-1/5"
            size="md"
            theme={Themes.primary}
            type="submit"
            iconPosition="after"
            loading={isUpdatePending}
          >
            {t("olkypass.general.continue")}
          </Button>
        </div>
      </div>
    </form>
  );
};
